import ClassModel from "#src/models/ClassModel.ts";
import GroupRegistrationModel, {
  isGroupRegistration,
} from "#src/models/GroupRegistrationModel.ts";
import PrivateSerieModel from "#src/models/PrivateSerieModel.ts";
import UserModel from "#src/models/UserModel.ts";
import { RegistrationDiscount } from "#src/utils/calculateDiscounts.ts";
import { calculateMembershipRegistrationPrice } from "#src/utils/calculateMembershipRegistrationPrice.ts";
import calculateProratedRegistrationPrice from "#src/utils/calculateProratedRegistrationPrice.ts";
import { moment } from "#src/utils/dateTime.ts";
import {
  AmountType,
  DiscountType,
  GroupRegistrationStatus,
} from "#types/code-generator.ts";
import Decimal from "decimal.js-light";

interface CalculateDiscountsForRegistrationInput {
  discounts: RegistrationDiscount[];
  registration: GroupRegistrationModel;
  registrations: Array<GroupRegistrationModel | PrivateSerieModel>;
  startDate: Date;
  organization: {
    departments: {
      id: string;
    }[];
    timezone: string;
  };
  index?: number;
  user: UserModel;
}

export default ({
  discounts,
  registration,
  registrations,
  startDate,
  organization,
  index = 0,
  user,
}: CalculateDiscountsForRegistrationInput) => {
  const result: Array<{ discount: RegistrationDiscount; amount: number }> = [];

  discounts.forEach((d) => {
    let amount = 0;

    if (d.amountType === AmountType.Dollar) {
      if (d.discountType === DiscountType.MultiStudent) {
        const eligibleMultiStudentRegistrations = registrations.filter((r) => {
          if (d.availableForAllOfferings) {
            if (isGroupRegistration(r) && organization.departments.length > 1) {
              return r.class?.department?.id === d.department?.id;
            }

            return true;
          }

          if (isGroupRegistration(r)) {
            const price = calculateProratedRegistrationPrice({
              classItem: new ClassModel(r.class),
              endDate: moment(startDate).endOf("month").toDate(),
              startDate,
              organization,
            });
            let departmentEligible = true;

            if (organization.departments.length > 1) {
              departmentEligible = r.class?.department?.id === d.department?.id;
            }

            // console.log("price", price);

            return (
              departmentEligible &&
              (price > 0 || r.class.chargeAmounts.length > 0) &&
              r.status !== GroupRegistrationStatus.Waitlist &&
              r.class?.discounts.find((cd) => cd.id === d.id)
            );
          }

          return false;
        });

        const participants = new Set(
          eligibleMultiStudentRegistrations
            .filter(isGroupRegistration)
            .map((r) => r.student?.id)
        );

        amount += Math.ceil(d.amount / participants.size);
      } else {
        amount += d.amount;
      }
    } else {
      if (isGroupRegistration(registration)) {
        let endDate = moment(startDate)
          .tz(organization.timezone)
          .endOf("month");

        if (registration.finalDate) {
          const finalDate = moment(registration.finalDate).tz(
            organization.timezone
          );
          if (finalDate.isAfter(startDate) && finalDate.isBefore(endDate)) {
            endDate = finalDate;
          }
        }

        // console.log("startDate", startDate);
        let price = calculateProratedRegistrationPrice({
          classItem: registration.class,
          endDate: endDate.toDate(),
          startDate,
          organization,
        });

        if (registration.class?.chargeAmounts?.length) {
          let atRegistrationChargeAmount =
            registration.class?.chargeAmounts.find((ca) => ca.atRegistration);

          for (const m of registration?.accountOwner?.memberships || []) {
            const membershipAtRegistrationChargeAmount =
              registration.class?.chargeAmounts.find(
                (ca) =>
                  ca.atRegistration &&
                  ca.membershipOffering?.id === m.offering?.id
              );

            if (
              membershipAtRegistrationChargeAmount?.price <
              atRegistrationChargeAmount?.price
            ) {
              atRegistrationChargeAmount = membershipAtRegistrationChargeAmount;
            }
          }

          if (
            atRegistrationChargeAmount &&
            atRegistrationChargeAmount.price > 0
          ) {
            price = atRegistrationChargeAmount.price;
          }
        }

        if (
          registration.class.membershipPrices.length > 0 &&
          user.memberships?.length > 0
        ) {
          const membershipPrice = calculateMembershipRegistrationPrice({
            offering: registration.class,
            user,
            registration,
          });

          price = calculateProratedRegistrationPrice({
            classItem: new ClassModel({
              ...registration.class,
              price: membershipPrice,
            }),
            endDate: endDate.toDate(),
            startDate,
            organization,
          });
        }

        // console.log("price", price);

        let discountAmount = new Decimal(d.amount).times(price);

        if (!discountAmount.isInteger()) {
          if (
            index === 0 ||
            (discountAmount.modulo(1).toNumber() > 0.5 && index === 1)
          ) {
            Decimal.config({ rounding: Decimal.ROUND_CEIL });
          } else {
            Decimal.config({ rounding: Decimal.ROUND_FLOOR });
          }

          discountAmount = discountAmount.toInteger();
        }

        amount += discountAmount.toNumber();
      }
    }

    // console.log("amount", amount);

    result.push({
      discount: d,
      amount,
    });
  });

  return result;
};
